<template>
    <div class="integrations-content">
        <SectionContainer
            title="Logs de Integração da Reserva"
            icon="exchange-alt"
        >
            <div class="integration-summary mb-4" v-if="summaryData">
                <div class="row">
                    <div class="col-md-4">
                        <div class="summary-card">
                            <div class="summary-icon success">
                                <i class="fas fa-check-circle"></i>
                            </div>
                            <div class="summary-content">
                                <div class="summary-value">{{ summaryData.success_count || 0 }}</div>
                                <div class="summary-label">Sucessos</div>
                            </div>
                        </div>
                    </div>

                    <div class="col-md-4">
                        <div class="summary-card">
                            <div class="summary-icon error">
                                <i class="fas fa-times-circle"></i>
                            </div>
                            <div class="summary-content">
                                <div class="summary-value">{{ summaryData.error_count || 0 }}</div>
                                <div class="summary-label">Erros</div>
                            </div>
                        </div>
                    </div>

                    <div class="col-md-4">
                        <div class="summary-card">
                            <div class="summary-icon info">
                                <i class="fas fa-list"></i>
                            </div>
                            <div class="summary-content">
                                <div class="summary-value">{{ summaryData.total_count || 0 }}</div>
                                <div class="summary-label">Total</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <IntegrationsList
                :reserve-id="$route.params.id"
                :show-filters="false"
                :auto-load="false"
                ref="integrationsList"
                @integration-retried="handleIntegrationRetried"
            />

            <div class="integration-info mt-4" v-if="reserve.id">
                <div class="alert alert-info">
                    <i class="fas fa-info-circle mr-2"></i>
                    <strong>Sobre as Integrações:</strong>
                    Os logs exibidos acima mostram todas as tentativas de integração relacionadas a esta reserva (ID: {{ reserve.id }}).
                    Isso inclui criação, atualizações e sincronizações com sistemas externos.
                </div>
            </div>
        </SectionContainer>
    </div>
</template>

<script>
import SectionContainer from "../../Modals/Checklist/SectionContainer.vue"
import IntegrationsList from "../../IntegrationLog/IntegrationsList.vue";
import http from "../../../http"

export default {
    name: 'ViewIntegrations',

    components: {
        SectionContainer,
        IntegrationsList
    },

    props: {
        reserve: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            summaryData: null,
            loadingSummary: false
        }
    },

    mounted() {
        this.loadData()
    },

    watch: {
        'reserve.id'(newId, oldId) {
            if (newId && newId !== oldId) {
                this.loadData()
            }
        }
    },

    methods: {
        /**
         * Carrega todos os dados necessários / Load all necessary data
         */
        async loadData() {
            if (!this.reserve.id) return

            try {
                await Promise.all([
                    this.loadSummaryData(),
                    this.initializeIntegrationsList()
                ])
            } catch (error) {
                console.error('Erro ao carregar dados das integrações:', error)
            }
        },

        /**
         * Carrega dados resumidos das integrações / Load integration summary data
         */
        async loadSummaryData() {
            if (!this.reserve.id) return

            this.loadingSummary = true

            try {
                const apiUrl = this.$store.getters.api + `integration-logs/summary?entity_id=${this.reserve.id}`
                const response = await http.get(apiUrl)

                this.summaryData = response.data.data || {
                    success_count: 0,
                    error_count: 0,
                    timeout_count: 0,
                    total_count: 0
                }
            } catch (error) {
                console.error('Erro ao carregar resumo das integrações:', error)
                this.summaryData = {
                    success_count: 0,
                    error_count: 0,
                    timeout_count: 0,
                    total_count: 0
                }
            } finally {
                this.loadingSummary = false
            }
        },

        /**
         * Inicializa a lista de integrações / Initialize integrations list
         */
        async initializeIntegrationsList() {
            await this.$nextTick()

            if (this.$refs.integrationsList) {
                this.$refs.integrationsList.initializeTable()
            }
        },

        /**
         * Manipula evento de tentativa de integração bem-sucedida / Handle successful integration retry event
         */
        handleIntegrationRetried(data) {
            this.loadSummaryData()
            this.$emit('integration-updated', data)
        },

        /**
         * Método público para recarregar todos os dados / Public method to reload all data
         */
        async reload() {
            await this.loadData()

            if (this.$refs.integrationsList) {
                await this.$refs.integrationsList.reloadTable()
            }
        }
    }
}
</script>

<style scoped>
.integrations-content {
    padding: 20px;
    background-color: white;
}

/* Cards de resumo / Summary cards */
.integration-summary {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 20px;
}

.summary-card {
    background-color: white;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border-left: 4px solid #e2e8f0;
    display: flex;
    align-items: center;
    gap: 15px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    margin-bottom: 15px;
}

.summary-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.summary-icon {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: white;
}

.summary-icon.success {
    background: linear-gradient(135deg, #28a745, #20c997);
    border-left-color: #28a745;
}

.summary-icon.error {
    background: linear-gradient(135deg, #dc3545, #c82333);
    border-left-color: #dc3545;
}

.summary-icon.warning {
    background: linear-gradient(135deg, #ffc107, #fd7e14);
    border-left-color: #ffc107;
}

.summary-icon.info {
    background: linear-gradient(135deg, #FE8D18, #0056b3);
    border-left-color: #FE8D18;
}

.summary-content {
    flex: 1;
}

.summary-value {
    font-size: 24px;
    font-weight: bold;
    color: #2c3e50;
    line-height: 1;
    margin-bottom: 4px;
}

.summary-label {
    font-size: 13px;
    color: #6c757d;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Atualizar cores das bordas dos cards */
.summary-card:nth-child(1) {
    border-left-color: #28a745;
}

.summary-card:nth-child(2) {
    border-left-color: #dc3545;
}

.summary-card:nth-child(3) {
    border-left-color: #ffc107;
}

.summary-card:nth-child(4) {
    border-left-color: #FE8D18;
}

/* Informações adicionais / Additional information */
.integration-info {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 15px;
}

.alert {
    border-radius: 8px;
    border: none;
    background: linear-gradient(135deg, #d1ecf1, #bee5eb);
    color: #0c5460;
    border-left: 4px solid #17a2b8;
}

/* Responsividade / Responsiveness */
@media (max-width: 768px) {
    .integrations-content {
        padding: 15px;
    }

    .integration-summary {
        padding: 15px;
    }

    .summary-card {
        padding: 12px;
        margin-bottom: 10px;
    }

    .summary-icon {
        width: 40px;
        height: 40px;
        font-size: 16px;
    }

    .summary-value {
        font-size: 20px;
    }

    .summary-label {
        font-size: 12px;
    }
}

@media (max-width: 576px) {
    .summary-card {
        flex-direction: column;
        text-align: center;
        gap: 10px;
    }

    .summary-icon {
        width: 50px;
        height: 50px;
        font-size: 18px;
    }

    .summary-value {
        font-size: 24px;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/ReserveVehicles/view-components/ViewIntegrations.vue